import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { BookOpen, ChevronDown, ArrowRight, Download, Loader2 } from 'lucide-react';
import PageHero from '../components/PageHero';
import { useTranslation } from '../i18nContext';
import { isValidLocale } from '../i18n';
import { api, absoluteUrl } from '../lib/api';
import { normalizeHtml, looksLikeHtml } from '../lib/htmlText';

function LegalText({ content, className }) {
  if (looksLikeHtml(content)) {
    return (
      <div
        className={`prose prose-sm max-w-none prose-p:my-2 prose-headings:text-charcoal-700 prose-strong:text-charcoal-700 prose-a:text-cmic-600 break-words ${className || ''}`}
        dangerouslySetInnerHTML={{ __html: normalizeHtml(content) }}
      />
    );
  }
  return <p className={`whitespace-pre-line ${className || ''}`}>{content}</p>;
}

export default function StatutesPage() {
  const { lang } = useParams();
  const t = useTranslation();
  const locale = (lang && isValidLocale(lang)) ? lang : t.locale;
  const isEs = locale === 'es';
  const [statutes, setStatutes] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/legal/estatutos')
      .then((r) => setStatutes(r.data))
      .catch(() => setStatutes(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <section className="py-32 flex items-center justify-center" data-testid="statutes-loading">
        <Loader2 className="animate-spin text-cmic-500" size={32} />
      </section>
    );
  }
  if (!statutes) {
    return (
      <>
        <PageHero title={isEs ? 'Estatutos' : 'Statutes'} />
        <section className="py-20 text-center">
          <p className="text-charcoal-500">{isEs ? 'No se pudo cargar el documento.' : 'Failed to load document.'}</p>
        </section>
      </>
    );
  }

  const totalArticles = statutes.chapters.reduce((acc, c) => acc + c.articles.length, 0);

  return (
    <>
      <PageHero
        label={isEs ? 'Documento oficial CMIC' : 'CMIC Official Document'}
        title={statutes.title?.[locale] || statutes.title?.es || ''}
      />

      <section className="py-16 sm:py-20 bg-white" data-testid="statutes-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8 flex-wrap">
            <div className="w-12 h-12 rounded-xl bg-cmic-100 flex items-center justify-center flex-shrink-0">
              <BookOpen size={22} className="text-cmic-600" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <h2 className="text-xl sm:text-2xl font-bold text-charcoal-800">
                {isEs ? 'Estatutos vigentes' : 'Current Statutes'}
              </h2>
              <p className="text-charcoal-400 text-sm mt-1">
                {isEs
                  ? `${statutes.chapters.length} capítulos · ${totalArticles} artículos`
                  : `${statutes.chapters.length} chapters · ${totalArticles} articles`}
              </p>
            </div>
            {statutes.show_download !== false && (
              <a
                href={absoluteUrl(statutes.pdf_url || '/estatutos-cmic.pdf')}
                download
                data-testid="statutes-download-pdf"
                className="inline-flex items-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white px-5 py-2.5 rounded-xl font-semibold shadow-sm hover:shadow-md transition-all text-sm"
              >
                <Download size={16} />
                {statutes.download_label?.[locale] || statutes.download_label?.es || (isEs ? 'Descargar PDF' : 'Download PDF')}
              </a>
            )}
          </div>

          {statutes.revision_note && (statutes.revision_note[locale] || statutes.revision_note.es) && (
            <div className="mb-10 p-4 sm:p-5 bg-cmic-50/60 border border-cmic-100 rounded-xl text-sm text-charcoal-600 leading-relaxed italic" data-testid="statutes-revision-note">
              <LegalText content={statutes.revision_note[locale] || statutes.revision_note.es} />
            </div>
          )}

          <div className="space-y-3" data-testid="statutes-chapters">
            {statutes.chapters.map((chapter, ci) => (
              <ChapterAccordion key={ci} chapter={chapter} locale={locale} defaultOpen={ci === 0} />
            ))}
          </div>

          <StatutesCTA locale={locale} />
        </div>
      </section>
    </>
  );
}

function ChapterAccordion({ chapter, locale, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-charcoal-100 rounded-xl overflow-hidden transition-all duration-200 hover:border-cmic-200" data-testid="statutes-chapter">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left bg-charcoal-50/50 hover:bg-cmic-50/50 transition-colors"
        data-testid="statutes-chapter-toggle"
      >
        <span className="font-semibold text-charcoal-700 text-sm sm:text-base pr-4">{chapter.title[locale]}</span>
        <ChevronDown
          size={18}
          className={`text-charcoal-400 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-5 py-5 border-t border-charcoal-100 bg-white">
          <div className="space-y-5">
              {chapter.articles.map((article, ai) => (
                <div key={ai} className="pl-4 border-l-2 border-cmic-200" data-testid="statutes-article">
                  {article.number && (
                    <span className="inline-block text-xs font-bold text-cmic-600 bg-cmic-50 rounded px-2 py-0.5 mb-2">
                      {locale === 'es' ? 'Artículo' : 'Article'} {article.number}
                    </span>
                  )}
                  <LegalText content={article[locale] || article.es} className="text-charcoal-600 text-sm leading-relaxed" />
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatutesCTA({ locale }) {
  const isEs = locale === 'es';
  return (
    <div
      className="mt-14 rounded-2xl overflow-hidden border border-cmic-200 bg-gradient-to-br from-cmic-50 via-white to-amber-50 p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-8"
      data-testid="statutes-cta"
    >
      <div className="flex-1">
        <p className="text-xs font-bold tracking-widest uppercase text-cmic-700 mb-2">
          {isEs ? 'Únete al CMIC' : 'Join CMIC'}
        </p>
        <h3 className="text-2xl sm:text-3xl font-bold text-charcoal-800 leading-tight">
          {isEs
            ? '¿Te identificas con estos estatutos? Conviértete en miembro.'
            : 'Do these statutes resonate with you? Become a member.'}
        </h3>
        <p className="mt-3 text-charcoal-600 text-sm sm:text-base leading-relaxed max-w-xl">
          {isEs
            ? 'Forma parte de la comunidad mexicana de intérpretes de conferencias.'
            : 'Become part of the Mexican community of conference interpreters.'}
        </p>
      </div>
      <Link
        to={`/${locale}/${isEs ? 'membresia' : 'membership'}`}
        data-testid="statutes-cta-button"
        className="inline-flex items-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all whitespace-nowrap"
      >
        {isEs ? 'Conviértete en miembro' : 'Become a member'}
        <ArrowRight size={18} />
      </Link>
    </div>
  );
}
